import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBuyerAuth } from '../contexts/BuyerAuthContext';
import { kgToBoxes, BOX_KG } from '../utils/packaging';

interface OfferLineItem {
  id: string;
  productId: string | null;
  grade: string;
  quantityKg: string;
  pricePerKg: string;
  lineTotal: string;
}

interface Offer {
  id: string;
  offerNumber: string;
  status: string;
  currency: string;
  incoterms: string;
  shippingMonth: string;
  notes: string;
  totalQuantityKg: string;
  totalValue: string;
  lineItems: OfferLineItem[];
}

interface Contract {
  id: string;
  contractNumber: string;
  status: string;
  coffeeDescription: string;
  totalQuantityKg: string;
  totalValue: string;
  pricePerKg: string;
  currency: string;
  incoterms: string;
  shippingMonth: string;
}

interface ChangeRequest {
  id: string;
  contractId: string;
  message: string;
  status: string;
  createdAt: string;
}

const fmtCurrency = (amount: string | number, currency: string = 'USD') => {
  const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', KES: 'KES ' };
  const sym = symbols[currency] || currency + ' ';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const Portal: React.FC = () => {
  const { buyer, loading, logout } = useBuyerAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<{ buyer: any; contracts: Contract[]; offers: Offer[]; changeRequests: ChangeRequest[] } | null>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [changeFormContractId, setChangeFormContractId] = useState<string | null>(null);
  const [changeMessage, setChangeMessage] = useState('');
  const [submittingChange, setSubmittingChange] = useState(false);
  // offerId -> lineItemId -> edited quantity (kg), as a string while the input is live
  const [editedQty, setEditedQty] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    if (!loading && !buyer) navigate('/portal/login', { replace: true });
  }, [loading, buyer, navigate]);

  const loadData = () => {
    fetch('/api/portal/data', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setDataLoading(false); })
      .catch(() => setDataLoading(false));
  };

  useEffect(() => {
    if (buyer) loadData();
  }, [buyer]);

  const getQty = (offer: Offer, li: OfferLineItem): number => {
    const edited = editedQty[offer.id]?.[li.id];
    if (edited === undefined) return parseFloat(li.quantityKg);
    const n = parseFloat(edited);
    return Number.isFinite(n) ? n : 0;
  };

  const setQty = (offerId: string, lineItemId: string, value: string) => {
    setEditedQty(prev => ({ ...prev, [offerId]: { ...prev[offerId], [lineItemId]: value } }));
  };

  const offerLiveTotals = (offer: Offer) => {
    let kg = 0, value = 0;
    for (const li of offer.lineItems) {
      const q = Math.min(Math.max(getQty(offer, li), 0), parseFloat(li.quantityKg));
      kg += q;
      value += q * parseFloat(li.pricePerKg);
    }
    return { kg, value, boxes: kgToBoxes(kg) };
  };

  const handleRespond = async (offer: Offer, action: 'accept' | 'decline') => {
    if (action === 'decline' && !confirm('Decline this offer?')) return;
    if (action === 'accept' && offerLiveTotals(offer).kg <= 0) return;
    setRespondingId(offer.id);
    try {
      const body: any = { action };
      if (action === 'accept') {
        body.lineItems = offer.lineItems.map(li => ({
          id: li.id,
          quantityKg: Math.min(Math.max(getQty(offer, li), 0), parseFloat(li.quantityKg)),
        }));
      }
      const res = await fetch(`/api/portal/offers?id=${offer.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) loadData();
    } finally {
      setRespondingId(null);
    }
  };

  const submitChangeRequest = async (contractId: string) => {
    if (!changeMessage.trim()) return;
    setSubmittingChange(true);
    try {
      const res = await fetch('/api/portal/change-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ contractId, message: changeMessage.trim() }),
      });
      if (res.ok) {
        setChangeMessage('');
        setChangeFormContractId(null);
        loadData();
      }
    } finally {
      setSubmittingChange(false);
    }
  };

  if (loading || !buyer) {
    return <div className="min-h-screen flex items-center justify-center bg-light-bg"><p className="text-light-text-tertiary text-sm">Loading&hellip;</p></div>;
  }

  const pendingOffers = data?.offers.filter(o => o.status === 'sent') || [];
  const pastOffers = data?.offers.filter(o => o.status !== 'sent' && o.status !== 'draft') || [];

  return (
    <div className="min-h-screen bg-light-bg">
      <header className="border-b border-light-border bg-white/60 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-display text-lg tracking-wide text-light-text-primary">Lot 20</Link>
          <button onClick={() => logout().then(() => navigate('/portal/login'))} className="text-xs text-light-text-tertiary hover:text-light-text-primary transition-colors">
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-[11px] uppercase tracking-eyebrow text-brand-accent mb-2">Welcome back</p>
        <h1 className="font-display text-3xl text-light-text-primary mb-1">{data?.buyer?.companyName || buyer.companyName}</h1>
        <p className="text-sm text-light-text-secondary mb-12">{buyer.email}</p>

        {dataLoading ? (
          <p className="text-sm text-light-text-tertiary">Loading your ledger&hellip;</p>
        ) : (
          <>
            {/* Pending offers */}
            {pendingOffers.length > 0 && (
              <section className="mb-14">
                <h2 className="text-[11px] uppercase tracking-eyebrow text-light-text-tertiary mb-5">Offers Awaiting Response</h2>
                <p className="text-xs text-light-text-tertiary mb-5">
                  Don't want the full amount of a lot? Adjust the quantity below — coffee ships in {BOX_KG}kg boxes (two 15kg bricks each), so amounts round to the nearest box.
                </p>
                <div className="space-y-4">
                  {pendingOffers.map(offer => {
                    const live = offerLiveTotals(offer);
                    return (
                      <div key={offer.id} className="bg-white border border-light-border rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <p className="font-semibold text-light-text-primary">{offer.offerNumber}</p>
                          <p className="text-xs text-light-text-tertiary">{offer.incoterms}{offer.shippingMonth ? ` · ${offer.shippingMonth}` : ''}</p>
                        </div>
                        <div className="space-y-3 mb-4">
                          {offer.lineItems.map(li => {
                            const originalQty = parseFloat(li.quantityKg);
                            const qty = Math.min(Math.max(getQty(offer, li), 0), originalQty);
                            const lineValue = qty * parseFloat(li.pricePerKg);
                            return (
                              <div key={li.id} className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-light-text-secondary flex-shrink-0">Grade {li.grade}</span>
                                <div className="flex items-center gap-2 flex-1 justify-end">
                                  <input
                                    type="number"
                                    min={0}
                                    max={originalQty}
                                    step="1"
                                    value={editedQty[offer.id]?.[li.id] ?? li.quantityKg}
                                    onChange={e => setQty(offer.id, li.id, e.target.value)}
                                    className="w-24 px-2 py-1.5 border border-light-border rounded-lg text-sm text-light-text-primary bg-light-bg text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                                  />
                                  <span className="text-light-text-tertiary text-xs w-14 flex-shrink-0">kg / {originalQty.toLocaleString('en-US')}</span>
                                  <span className="text-light-text-tertiary text-xs w-16 flex-shrink-0">{kgToBoxes(qty)} boxes</span>
                                  <span className="text-light-text-primary font-medium tabular-nums w-20 text-right flex-shrink-0">{fmtCurrency(lineValue, offer.currency)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-light-border">
                          <div>
                            <p className="text-lg font-bold text-light-text-primary tabular-nums">{fmtCurrency(live.value, offer.currency)}</p>
                            <p className="text-xs text-light-text-tertiary">{live.kg.toLocaleString('en-US')}kg &middot; {live.boxes.toLocaleString('en-US')} boxes</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleRespond(offer, 'decline')}
                              disabled={respondingId === offer.id}
                              className="px-4 py-2 text-sm text-light-text-secondary border border-light-border rounded-lg hover:bg-light-elevated transition-colors disabled:opacity-40"
                            >
                              Decline
                            </button>
                            <button
                              onClick={() => handleRespond(offer, 'accept')}
                              disabled={respondingId === offer.id || live.kg <= 0}
                              title={live.kg <= 0 ? 'Set a quantity greater than zero for at least one lot' : undefined}
                              className="px-4 py-2 text-sm text-white bg-brand-accent rounded-lg hover:bg-brand-accent-dark transition-colors disabled:opacity-40"
                            >
                              {respondingId === offer.id ? 'Working...' : 'Accept'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Contracts / ledger */}
            <section className="mb-14">
              <h2 className="text-[11px] uppercase tracking-eyebrow text-light-text-tertiary mb-5">Your Ledger</h2>
              {data?.contracts.length ? (
                <div className="space-y-4">
                  {data.contracts.map(contract => {
                    const requests = data.changeRequests.filter(r => r.contractId === contract.id);
                    return (
                      <div key={contract.id} className="bg-white border border-light-border rounded-2xl p-6">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-light-text-primary">{contract.contractNumber}</p>
                            <p className="text-sm text-light-text-secondary mt-0.5">{contract.coffeeDescription}</p>
                          </div>
                          <span className="text-[11px] uppercase tracking-wide px-2 py-1 rounded-full bg-light-elevated text-light-text-secondary flex-shrink-0">{contract.status}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-light-text-secondary mt-3">
                          <span>{parseFloat(contract.totalQuantityKg).toLocaleString('en-US')}kg</span>
                          <span className="font-medium text-light-text-primary">{fmtCurrency(contract.totalValue, contract.currency)}</span>
                          <span>{contract.incoterms}</span>
                          {contract.shippingMonth && <span>{contract.shippingMonth}</span>}
                        </div>

                        {requests.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-light-border space-y-2">
                            {requests.map(r => (
                              <div key={r.id} className="text-xs">
                                <span className={`inline-block px-1.5 py-0.5 rounded-full mr-2 ${r.status === 'open' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{r.status}</span>
                                <span className="text-light-text-secondary">{r.message}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {changeFormContractId === contract.id ? (
                          <div className="mt-4 pt-4 border-t border-light-border">
                            <textarea
                              value={changeMessage}
                              onChange={e => setChangeMessage(e.target.value)}
                              placeholder="What would you like to change about this contract?"
                              rows={2}
                              autoFocus
                              className="w-full px-3 py-2 border border-light-border rounded-lg text-sm bg-light-bg text-light-text-primary placeholder-light-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                            />
                            <div className="flex gap-2 mt-2">
                              <button onClick={() => submitChangeRequest(contract.id)} disabled={submittingChange || !changeMessage.trim()} className="px-3 py-1.5 text-xs text-white bg-brand-accent rounded-lg hover:bg-brand-accent-dark transition-colors disabled:opacity-40">
                                {submittingChange ? 'Sending...' : 'Submit request'}
                              </button>
                              <button onClick={() => { setChangeFormContractId(null); setChangeMessage(''); }} className="px-3 py-1.5 text-xs text-light-text-secondary hover:text-light-text-primary transition-colors">
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setChangeFormContractId(contract.id)} className="mt-4 text-xs text-light-text-tertiary hover:text-brand-accent transition-colors underline underline-offset-2">
                            Request a change
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white border border-light-border rounded-2xl p-8 text-center">
                  <p className="text-sm text-light-text-tertiary">No contracts on file yet. When you accept an offer, it'll show up here.</p>
                </div>
              )}
            </section>

            {/* Past offers */}
            {pastOffers.length > 0 && (
              <section>
                <h2 className="text-[11px] uppercase tracking-eyebrow text-light-text-tertiary mb-5">Past Offers</h2>
                <div className="space-y-2">
                  {pastOffers.map(offer => (
                    <div key={offer.id} className="flex items-center justify-between px-5 py-3 bg-white border border-light-border rounded-xl text-sm">
                      <span className="text-light-text-primary font-medium">{offer.offerNumber}</span>
                      <span className="text-light-text-tertiary text-xs">{parseFloat(offer.totalQuantityKg).toLocaleString('en-US')}kg &middot; {kgToBoxes(parseFloat(offer.totalQuantityKg))} boxes</span>
                      <span className="text-light-text-secondary">{fmtCurrency(offer.totalValue, offer.currency)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${offer.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{offer.status}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};
